import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { outboundV2Service } from '@/services'
import { HouseBillSchema } from '@/services/CustomsV2Service'

interface DeliveryState {
  loading: boolean
  houseBills: HouseBillSchema[]
  refreshing: boolean
}

interface DeliveryConfirmationData {
  scheduledOutboundDate: string
  deliveryOpt: string
  deliveryNote: string
}

interface DeliveryActions {
  fetchDeliverys: () => Promise<void>
  refresh: () => Promise<void>
  confirmation: (houseBillId: number, data: DeliveryConfirmationData) => Promise<void>
  reject: (houseBillId: number) => Promise<void>
  updateHouseBill: (houseBillId: number, field: keyof HouseBillSchema, value: string) => void
}

type DeliveryStore = DeliveryState & DeliveryActions

const initialState: DeliveryState = {
  loading: false,
  refreshing: false,
  houseBills: []
}

export const useDeliveryStore = create<DeliveryStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // 获取拆柜任务列表
      fetchDeliverys: async () => {
        set({ loading: true })

        const { success, data } = await outboundV2Service.fetchDeliverys()

        if (success && data) {
          set({
            houseBills: data,
            loading: false
          })
        }
      },

      confirmation: async (houseBillId, data) => {
        const { success } = await outboundV2Service.deliveryConfirmation(
          houseBillId,
          data
        )
        if (success) {
          get().refresh()
        }
      },

      reject: async (houseBillId) => {
        const { success } = await outboundV2Service.deliveryReject(houseBillId)
        if (success) {
          get().refresh()
        }
      },

      // 更新本地 HouseBill 数据
      updateHouseBill: (houseBillId, field, value) => {
        set((state) => ({
          houseBills: state.houseBills.map((houseBill) =>
            houseBill.id === houseBillId
              ? { ...houseBill, [field]: value }
              : houseBill
          )
        }))
      },

      // 刷新数据
      refresh: async () => {
        await get().fetchDeliverys()
      }
    }),
    {
      name: 'delivery-store'
    }
  )
)

// 导出状态选择器
export const useDeliveryState = () => {
  const loading = useDeliveryStore((state) => state.loading)
  const houseBills = useDeliveryStore((state) => state.houseBills)
  const refreshing = useDeliveryStore((state) => state.refreshing)

  return {
    loading,
    houseBills,
    refreshing
  }
}

// 导出操作选择器
export const useDeliveryActions = () => {
  const fetchDeliverys = useDeliveryStore((state) => state.fetchDeliverys)
  const refresh = useDeliveryStore((state) => state.refresh)
  const confirmation = useDeliveryStore((state) => state.confirmation)
  const reject = useDeliveryStore((state) => state.reject)
  const updateHouseBill = useDeliveryStore((state) => state.updateHouseBill)
  return {
    fetchDeliverys,
    refresh,
    confirmation,
    reject,
    updateHouseBill
  }
}
